import AppHeader from "components/AppHeader";
import CallToactionBtn from "components/CallToactionBtn";
import useEleves, { toCurrentEleve } from "hooks/useEleves";
import mixpanel from "mixpanel-browser";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useGetEleveQuery } from "redux_dir/features/eleveApi";
import { getCurrentEleve, setCurrentEleve } from "redux_dir/features/eleveSlice";
import {
  EnfantSelectorCard,
  EnfantSheet,
} from "./components/EnfantSwitcher";

/**
 * Page d'accueil du suivi d'un enfant.
 *
 * C'est désormais la première page après connexion : le choix de l'enfant
 * se fait ici (feuille de sélection) au lieu d'une page intermédiaire.
 */
function MatierePage() {
  const params = useParams();
  const location = useLocation();
  const navigation = useNavigate();
  const dispatch = useDispatch();

  const [sheetOpened, setSheetOpened] = useState(false);

  const currentEleve = useSelector(getCurrentEleve);
  const { eleves, isLoading: elevesLoading, selectEleve } = useEleves();

  const { data: eleve, isLoading: eleveLoading } = useGetEleveQuery(
    params?.eleveId,
    { skip: !params?.eleveId }
  );

  /**
   * Arrivée directe sur l'URL (lien partagé, rafraîchissement, retour de
   * diagnostic) : on resynchronise l'enfant courant, sinon `classeId` manque
   * et la page reste bloquée sur un loader.
   */
  useEffect(() => {
    if (!eleve?.id) return;

    const next = toCurrentEleve(eleve);
    const sameEleve = String(currentEleve?.id) === String(next?.id);

    // On resynchronise aussi quand le profil vient d'être modifié (nom, classe) :
    // sans ça, `classeId` resterait sur l'ancienne classe et la liste des
    // diagnostics continuerait d'afficher le mauvais niveau.
    const aJour =
      sameEleve &&
      currentEleve?.classeId &&
      String(currentEleve?.classeId) === String(next?.classeId) &&
      currentEleve?.fullName === next?.fullName &&
      currentEleve?.classe === next?.classe;

    if (aJour) return;

    dispatch(setCurrentEleve(next));
  }, [
    eleve?.id,
    eleve?.firstName,
    eleve?.lastName,
    eleve?.classe?.id,
    eleve?.classe?.name,
    currentEleve?.id,
    currentEleve?.classeId,
    currentEleve?.fullName,
    currentEleve?.classe,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    dispatch,
  ]);

  const isRootMatiere = useMemo(
    () => /\/matiere\/?$/.test(location.pathname),
    [location.pathname]
  );

  const headerEleve = eleve?.id
    ? {
        id: eleve?.id,
        fullName: [eleve?.firstName, eleve?.lastName]
          .filter(Boolean)
          .join(" "),
        classe: eleve?.classe?.name,
      }
    : currentEleve;

  const handleSelect = (next) => {
    setSheetOpened(false);
    if (String(next?.id) === String(params?.eleveId)) return;
    selectEleve(next, { replace: true, track: "Changement Enfant" });
  };

  const handleAdd = () => {
    setSheetOpened(false);
    mixpanel.track("Clic Ajouter Enfant", { Source: "Sélecteur enfant" });
    navigation("/parentProspect/form", { state: { nombre: "n", exist: true } });
  };

  const handleEdit = (eleveToEdit) => {
    if (!eleveToEdit?.id) return;
    setSheetOpened(false);
    mixpanel.track("Clic Modifier Enfant", { "Enfant ID": eleveToEdit?.id });
    navigation(`/parentProspect/edit-enfant/${eleveToEdit?.id}`);
  };

  return (
    <div className="h-[100dvh] w-full flex flex-col overflow-hidden bg-white">
      {/* Bandeau */}
      <div
        className="shrink-0 bg-gradient-to-br from-primary to-secondary"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <AppHeader
          showBack={!isRootMatiere}
          title="Suivi de mon enfant"
          subtitle={isRootMatiere ? "Espace parent Genimi" : null}
        />
        <div className="px-4 pb-8 pt-1">
          <EnfantSelectorCard
            eleve={headerEleve}
            opened={sheetOpened}
            isLoading={eleveLoading}
            onToggle={() => {
              setSheetOpened((open) => !open);
              mixpanel.track("Ouverture Sélecteur Enfant");
            }}
          />
        </div>
      </div>

      {/* Contenu */}
      <div className="flex-1 min-h-0 overflow-y-auto bg-[#F7F9FC] rounded-t-[26px] -mt-5 relative z-10 shadow-[0_-6px_20px_-12px_rgba(0,0,0,0.35)]">
        <div className="p-2">
          <Outlet />
        </div>
        <div className="h-24" />
      </div>

      <EnfantSheet
        opened={sheetOpened}
        onClose={() => setSheetOpened(false)}
        eleves={eleves}
        currentId={params?.eleveId}
        onSelect={handleSelect}
        onAdd={handleAdd}
        onEdit={handleEdit}
        isLoading={elevesLoading}
      />

      <CallToactionBtn />
    </div>
  );
}

export default MatierePage;
